if (!customElements.get("sticky-header")) {
  customElements.define(
    "sticky-header",
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.stickyDisabled = this.getAttribute("disabled") === "true";
        this.header = document.querySelector(".section-header");
        this.headerElement = this.querySelector(".header");

        this.alwaysSticky = this.dataset.alwaysOn === "true";
        this.headerBounds = {};
        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.mainNavToggle = this.querySelector("main-nav-toggle");
        this.predictiveSearch = this.querySelector("predictive-search");

        if (this.header && !this.stickyDisabled && this.alwaysSticky) {
          this.header.classList.add("section-header-sticky-always");
        }

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => {
          this.preventReveal = true;
        };

        this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.addEventListener("scroll", this.onScrollHandler, false);

        this.createObserver();
        this._setupInnerMastMainPadding();
      }

      disconnectedCallback() {
        this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.removeEventListener("scroll", this.onScrollHandler);
        this._teardownInnerMastMainPadding();
      }

      /**
       * Document-space union of chrome used for scroll thresholds.
       * `.section-header` in-flow height often excludes `position:absolute` mast (transparent homepage)
       * and can exclude a top bar rendered as a sibling before `<sticky-header>` (inner-mast layout).
       */
      syncHeaderBoundsFromDom() {
        if (!this.header) {
          return;
        }
        const sy = window.pageYOffset || document.documentElement.scrollTop;
        const docBox = (el) => {
          if (!el) {
            return null;
          }
          const b = el.getBoundingClientRect();
          return { top: b.top + sy, bottom: b.bottom + sy };
        };

        const base = this.header.getBoundingClientRect();
        let top = base.top + sy;
        let bottom = base.bottom + sy;

        const mast = this.querySelector(".head-mast-wrap");
        const mastBox = mast ? docBox(mast) : null;
        if (mastBox) {
          top = Math.min(top, mastBox.top);
          bottom = Math.max(bottom, mastBox.bottom);
        }

        const prev = this.previousElementSibling;
        if (prev && prev.classList && prev.classList.contains("header-top-mast-anchor")) {
          const barBox = docBox(prev);
          if (barBox) {
            top = Math.min(top, barBox.top);
            bottom = Math.max(bottom, barBox.bottom);
          }
        }

        this.headerBounds = {
          top,
          bottom,
          height: Math.max(0, bottom - top),
        };
      }

      createObserver() {
        if (!this.header) {
          return;
        }
        const observer = new IntersectionObserver((entries, observer) => {
          this.syncHeaderBoundsFromDom();
          observer.disconnect();
        });

        observer.observe(this.header);
      }

      /** Inner-mast layout: pin with CSS fixed + offset main so content does not slide under the bar. */
      _innerMastPinned() {
        if (!this.header) {
          return false;
        }
        return (
          this.header.classList.contains("section-header-sticky") ||
          this.header.classList.contains("section-header-sticky-always")
        );
      }

      _syncInnerMastMainPadding() {
        if (!this.classList.contains("header-inner-mast-sticky")) {
          return;
        }
        const main = document.getElementById("MainContent");
        if (!main) {
          return;
        }
        if (this._innerMastPinned()) {
          const pos = window.getComputedStyle(this).position;
          /* Compact / short viewport CSS forces the mast out of fixed — avoid leaving a gap. */
          if (pos !== "fixed" && pos !== "sticky") {
            main.style.paddingTop = "";
            return;
          }
          const h = Math.ceil(this.getBoundingClientRect().height);
          main.style.paddingTop = `${h}px`;
        } else {
          main.style.paddingTop = "";
        }
      }

      _setupInnerMastMainPadding() {
        if (!this.classList.contains("header-inner-mast-sticky") || !this.header) {
          return;
        }
        this._innerMastPaddingObserver = new MutationObserver(() => {
          this._syncInnerMastMainPadding();
        });
        this._innerMastPaddingObserver.observe(this.header, { attributes: true, attributeFilter: ["class"] });
        this._innerMastResizeHandler = () => this._syncInnerMastMainPadding();
        window.addEventListener("resize", this._innerMastResizeHandler);
        this._syncInnerMastMainPadding();
      }

      _teardownInnerMastMainPadding() {
        if (this._innerMastPaddingObserver) {
          this._innerMastPaddingObserver.disconnect();
          this._innerMastPaddingObserver = null;
        }
        if (this._innerMastResizeHandler) {
          window.removeEventListener("resize", this._innerMastResizeHandler);
          this._innerMastResizeHandler = null;
        }
        if (this.classList.contains("header-inner-mast-sticky")) {
          const main = document.getElementById("MainContent");
          if (main) {
            main.style.paddingTop = "";
          }
        }
      }

      onScroll() {
        if (!this.header) {
          return;
        }

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (this.predictiveSearch && this.predictiveSearch.isOpen) {
          return;
        }

        const navigationWrapper = document.querySelector(".navigation-wrapper");
        if (navigationWrapper && navigationWrapper.classList.contains("is-animating")) {
          return;
        }

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          if (this.alwaysSticky && this.mainNavToggle) {
            this.mainNavToggle.enable();
          }
          requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          if (!this.preventReveal && !this.stickyDisabled) {
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            window.clearTimeout(this.isScrolling);

            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);

            requestAnimationFrame(this.hide.bind(this));
          }
        } else if (!this.stickyDisabled && scrollTop <= this.headerBounds.top) {
          requestAnimationFrame(this.reset.bind(this));
        } else if (this.stickyDisabled && scrollTop <= this.headerBounds.bottom) {
          requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.alwaysSticky) {
          return;
        }
        if (!this.header) {
          return;
        }
        const innerMast = this.classList.contains("header-inner-mast-sticky");
        const stickyTopBarCombined = this.classList.contains("header-sticky-top-bar-combined");
        if ((innerMast || stickyTopBarCombined) && this.header.classList.contains("section-header-sticky")) {
          return;
        }
        this.header.classList.add("section-header-sticky");
        /*
         * Do not add section-header-hidden when:
         * - inner mast: mast is sticky/fixed inside a section that must not translate away, or
         * - sticky top bar inside same header: hidden would slide the utility bar up while the mast stays fixed.
         */
        if (!innerMast && !stickyTopBarCombined) {
          this.header.classList.add("section-header-hidden");
        }

        // Enable nav collapse mode and pre-collapse it without animation
        if (this.mainNavToggle) {
          this.mainNavToggle.enable();
        }

        this.closeHeaderDropdowns();
        this._syncInnerMastMainPadding();
      }

      reveal() {
        if (this.alwaysSticky || this.stickyDisabled) {
          return;
        }
        if (!this.header) {
          return;
        }
        if (
          !this.header.classList.contains("section-header-hidden") &&
          this.header.classList.contains("section-header-sticky") &&
          this.header.classList.contains("animate")
        ) {
          return;
        }
        this.header.classList.add("section-header-sticky", "animate");
        this.header.classList.remove("section-header-hidden");
        this._syncInnerMastMainPadding();
      }

      reset() {
        if (!this.header) {
          return;
        }
        if (
          !this.header.classList.contains("section-header-hidden") &&
          !this.header.classList.contains("section-header-sticky") &&
          !this.header.classList.contains("animate")
        ) {
          return;
        }
        this.header.classList.remove("section-header-hidden", "section-header-sticky", "animate");
        this._syncInnerMastMainPadding();

        // If the navigation is collapsible, make sure it's always
        // expanded when we reach the top (when we reset).
        if (this.mainNavToggle) {
          this.mainNavToggle.disable();
        }
      }

      closeHeaderDropdowns() {
        const headerDropdowns = this.header.querySelectorAll("localization-form");

        headerDropdowns.forEach((dropdown) => {
          dropdown.hidePanel();
        });
      }
    },
  );
}

if (!customElements.get("main-nav-toggle")) {
  customElements.define(
    "main-nav-toggle",
    class MainNavToggle extends HTMLElement {
      constructor() {
        super();
        this.header = document.querySelector("#header");
        this.stickyHeader = document.querySelector("sticky-header");
        this.button = this.querySelector(".button-main-nav-toggle");
        this.navigationWrapper = document.querySelector(".navigation-wrapper");

        if (!this.navigationWrapper) {
          return;
        }

        this.navigationWrapper.style.setProperty(
          "--navigation-height",
          this.navigationWrapper.clientHeight + 2 + "px",
        );

        this.button.addEventListener("click", () => this.onToggle());
      }

      onToggle(state) {
        if (this.navigationWrapper.classList.contains("is-animating") || !this.stickyHeader) {
          return;
        }

        this.navigationWrapper.classList.add("is-animating");

        if (state != null) {
          if (state === "collapse") {
            this.collapse();
          } else if (state === "expand") {
            this.expand();
          }
        } else {
          this.isCollapsed() ? this.expand() : this.collapse();
        }

        setTimeout(() => {
          this.navigationWrapper.classList.remove("is-animating");
        }, 300);
      }

      enable() {
        if (!this.header.classList.contains("header-nav-collapse-enabled")) {
          this.header.classList.add("header-nav-collapse-enabled");
          this.collapse();
        }
      }

      disable() {
        if (this.header.classList.contains("header-nav-collapse-enabled")) {
          this.header.classList.remove("header-nav-collapse-enabled");
          this.expand();
        }
      }

      expand() {
        this.header.classList.remove("navigation-collapsed");
      }

      collapse() {
        this.classList.add("is-visible");
        this.header.classList.add("navigation-collapsed");
      }

      isCollapsed() {
        return this.header.classList.contains("navigation-collapsed");
      }
    },
  );
}
